import React, { useState, useEffect, useMemo } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { fetchDuePlots, syncZohoDuePlots, createInvoicesFromDuePlots } from "../api/api";

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

function formatCurrency(amount) {
  if (amount === null || amount === undefined || amount === "") return null;
  const num = Number(amount);
  if (Number.isNaN(num)) return null;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
}

function DuePlots() {

  const [reportType, setReportType] = useState("due");
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");
  const [invoicingId, setInvoicingId] = useState(null);
  const [bulkInvoicing, setBulkInvoicing] = useState(false);
  const [invoiceMessage, setInvoiceMessage] = useState("");
  const [invoicedZohoIds, setInvoicedZohoIds] = useState(() => new Set());

  const load = async (type) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchDuePlots(type);
      setPlots(data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(reportType);
    // eslint-disable-next-line
  }, [reportType]);

  const handleSyncNow = async () => {
    setSyncing(true);
    setSyncMessage("");
    setError("");
    try {
      const result = await syncZohoDuePlots(false);
      setSyncMessage(
        `Synced ${result.due.rows_stored} due, ${result.overdue.rows_stored} overdue, ` +
        `${result.water_collection_due.rows_stored} water collection due, ` +
        `${result.plot_collection_due.rows_stored} plot collection due plot(s).`
      );
      await load(reportType);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateInvoice = async (row) => {
    setInvoicingId(row.id);
    setInvoiceMessage("");
    setError("");
    try {
      const result = await createInvoicesFromDuePlots([row.report_type], [row.id]);
      if (result.created.length > 0) {
        setInvoicedZohoIds((prev) => new Set(prev).add(row.id));
        setInvoiceMessage(`Invoice ${result.created[0].invoice_number} created for plot ${row.plot_number} — sent to Invoice Approvals.`);
      } else {
        setInvoiceMessage(`Plot ${row.plot_number} already has an invoice from this Due Plots row.`);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setInvoicingId(null);
    }
  };

  const handleCreateInvoicesForAll = async () => {
    setBulkInvoicing(true);
    setInvoiceMessage("");
    setError("");
    try {
      // Scoped to whatever's currently on screen (respects the search box),
      // not every row of this report_type — the button label reflects this.
      const result = await createInvoicesFromDuePlots([reportType], filteredPlots.map((p) => p.id));
      setInvoiceMessage(
        `${result.created.length} invoice(s) created and sent to Invoice Approvals. ` +
        `Skipped: ${result.skipped_already_invoiced} already invoiced, ` +
        `${result.skipped_no_plot_match} with no matching plot, ` +
        `${result.skipped_no_amount} with no due amount.`
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBulkInvoicing(false);
    }
  };

  const filteredPlots = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return plots;
    return plots.filter((p) => {
      const haystack = `${p.plot_number} ${p.supervisor_name || ""} ${p.customer_name || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [plots, search]);

  const groupedBySupervisor = useMemo(() => {
    const groups = {};
    for (const p of filteredPlots) {
      const key = p.supervisor_name || "Unassigned";
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredPlots]);

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <h1 style={{ marginTop: 0, marginBottom: 0 }}>Due Plots</h1>
          <button className="login-btn" style={{ width: "auto", padding: "10px 20px" }} onClick={handleSyncNow} disabled={syncing}>
            {syncing ? "Syncing…" : "Sync Now"}
          </button>
        </div>

        <p style={{ color: "#666" }}>
          Plots due (or overdue) for maintenance, and outstanding collections, grouped by supervisor —
          mirrored from Zoho's Due_Plots/Overdue_Plots/Water_Collection_Due/Plot_Collection_Due reports.
        </p>

        {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}
        {syncMessage && <p style={{ color: "#166534", fontWeight: 600 }}>{syncMessage}</p>}
        {invoiceMessage && <p style={{ color: "#166534", fontWeight: 600 }}>{invoiceMessage}</p>}

        <div className="filter-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: "8px" }}>
            <button className={reportType === "due" ? "active-filter" : ""} onClick={() => setReportType("due")}>
              Due
            </button>
            <button className={reportType === "overdue" ? "active-filter" : ""} onClick={() => setReportType("overdue")}>
              Overdue
            </button>
            <button className={reportType === "water_collection_due" ? "active-filter" : ""} onClick={() => setReportType("water_collection_due")}>
              Water Collection Due
            </button>
            <button className={reportType === "plot_collection_due" ? "active-filter" : ""} onClick={() => setReportType("plot_collection_due")}>
              Plot Collection Due
            </button>
          </div>

          <button
            className="login-btn"
            style={{ width: "auto", padding: "8px 16px" }}
            onClick={handleCreateInvoicesForAll}
            disabled={bulkInvoicing || filteredPlots.length === 0}
          >
            {bulkInvoicing ? "Creating…" : `Create Invoices for All (${filteredPlots.length})`}
          </button>
        </div>

        <input
          type="text"
          placeholder="Search by plot, supervisor, or customer"
          className="search-bar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: "20px", width: "100%" }}
        />

        {loading && <p>Loading…</p>}

        {!loading && filteredPlots.length === 0 && <p>No {reportType} plots found.</p>}

        {!loading && groupedBySupervisor.map(([supervisorName, supervisorPlots]) => (
          <div className="section-card" key={supervisorName} style={{ marginBottom: "20px" }}>

            <h3 style={{ marginTop: 0 }}>{supervisorName} ({supervisorPlots.length})</h3>

            <div style={{ background: "white", borderRadius: "10px", overflow: "hidden" }}>
              <table className="plot-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ background: "#f5f5f5" }}>
                  <tr>
                    <th>Plot</th>
                    <th>Customer</th>
                    <th>Zone</th>
                    <th>Maintenance Type</th>
                    <th>Status</th>
                    {reportType === "due" && <th>Next Due</th>}
                    <th>Last Maintenance</th>
                    <th>Amount Due</th>
                    <th>Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {supervisorPlots.map((p) => (
                    <tr key={p.id}>
                      <td><b>{p.plot_number}</b></td>
                      <td>{p.customer_name || "—"}</td>
                      <td>{p.zone_number || "—"}</td>
                      <td>{p.maintenance_type || "—"}</td>
                      <td>{p.maintenance_status || "—"}</td>
                      {reportType === "due" && <td>{p.next_maintenance_date || "—"}</td>}
                      <td>{p.last_maintenance_date || "—"}</td>
                      <td>{formatCurrency(p.maintenance_due_amount) || "—"}</td>
                      <td>
                        {invoicedZohoIds.has(p.id) ? (
                          <span style={{ color: "#166534", fontWeight: 600 }}>Created</span>
                        ) : (
                          <button
                            className="pay-btn"
                            style={{ padding: "4px 10px", fontSize: "13px" }}
                            onClick={() => handleCreateInvoice(p)}
                            disabled={invoicingId === p.id || !p.maintenance_due_amount}
                            title={!p.maintenance_due_amount ? "No due amount to invoice" : undefined}
                          >
                            {invoicingId === p.id ? "Creating…" : "Create Invoice"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        ))}

      </div>

    </div>

  );

}

export default DuePlots;
